/**
 * apiFetch — thin fetch wrapper that:
 *  - attaches the Bearer access token from the auth context
 *  - sends credentials (httpOnly refresh cookie) on every request
 *  - on 401: attempts a silent token refresh (single in-flight refresh, no stampede),
 *    then retries once
 *  - on second 401: calls onUnauthenticated() so the app can redirect to login
 */

type TokenGetter = () => string | null
type TokenSetter = (token: string | null) => void
type UnauthHandler = () => void

let _getToken: TokenGetter = () => null
let _setToken: TokenSetter = () => undefined
let _onUnauthenticated: UnauthHandler = () => undefined

export function configureApiClient(
  getToken: TokenGetter,
  setToken: TokenSetter,
  onUnauthenticated: UnauthHandler,
): void {
  _getToken = getToken
  _setToken = setToken
  _onUnauthenticated = onUnauthenticated
}

// Single in-flight refresh promise — prevents token-refresh stampede when
// multiple concurrent requests all receive 401 at the same time.
let _pendingRefresh: Promise<string | null> | null = null

async function _refresh(): Promise<string | null> {
  if (_pendingRefresh) return _pendingRefresh
  _pendingRefresh = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      })
      if (!res.ok) return null
      const data = (await res.json()) as { access_token: string }
      _setToken(data.access_token)
      return data.access_token
    } finally {
      _pendingRefresh = null
    }
  })()
  return _pendingRefresh
}

function _buildHeaders(token: string | null, init?: RequestInit): Headers {
  const headers = new Headers(init?.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (!headers.has('Content-Type') && !(init?.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }
  return headers
}

export async function apiFetch(
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const token = _getToken()
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: _buildHeaders(token, init),
  })

  if (res.status !== 401) return res

  // Try to refresh
  const newToken = await _refresh()
  if (!newToken) {
    _onUnauthenticated()
    return res
  }

  // Retry with new token
  const retried = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: _buildHeaders(newToken, init),
  })

  if (retried.status === 401) {
    _onUnauthenticated()
  }

  return retried
}
